import type { LearningExercise } from "../types";

// Hagen BGB-AT, Stellvertretung §§ 164–181 (größte Content-Lücke laut
// docs/research/2026-06-03-content-gap.md). Von Vincent freigegeben (2026-06-03,
// nach question-reviewer-Gate 14 OK / 0 FIX / 0 AMBIG) -> reviewStatus "beta-ready",
// in questions.ts gespreadet. Format wie src/data/flashcards.ts (options: string[], answers: [Index]).
// Ergänzt den bestehenden Fall case-stellvertretung-fahrrad (§§ 164/177/179) um die
// übrigen Kernfiguren: §164 Abs.2, Offenkundigkeit, Bote, §§165/166/167, Rechtsschein,
// §§174/180/181 und Missbrauch der Vertretungsmacht.
export const hagenStellvertretung: LearningExercise[] = [
  {
    id: "hj-stellv-001",
    pathId: "hagen-jura",
    mode: "ausbildung",
    kind: "single",
    topic: "BGB AT - Stellvertretung",
    title: "Offenkundigkeitsprinzip",
    prompt: "Was verlangt das Offenkundigkeitsprinzip der Stellvertretung?",
    options: [
      "Der Vertreter muss volljährig und voll geschäftsfähig sein.",
      "Der Vertreter muss erkennbar im fremden Namen handeln - ausdrücklich oder aus den Umständen.",
      "Der Vertreter muss stets eine schriftliche Vollmachtsurkunde vorlegen.",
      "Der Vertreter muss wirtschaftlich im Interesse des Vertretenen handeln.",
    ],
    answers: [1],
    explanation:
      "§ 164 Abs. 1 BGB: Die Vertretung wirkt nur für und gegen den Vertretenen, wenn der Vertreter in dessen Namen handelt (S. 1); dabei macht es keinen Unterschied, ob er ausdrücklich im fremden Namen auftritt oder sich dies aus den Umständen ergibt (S. 2). Auf Geschäftsfähigkeit (§ 165), Vollmachtsurkunde (§ 174) oder wirtschaftliche Interessen kommt es dafür nicht an.",
    sourceIds: ["bgb164"],
    sources: [{ id: "bgb164", label: "§ 164 BGB", url: "https://www.gesetze-im-internet.de/bgb/__164.html" }],
    level: "Bekannt",
    levelNumber: 1,
    errorCategory: "Definition",
    skillTags: ["bgb-at", "stellvertretung"],
    reviewStatus: "beta-ready",
  },
  {
    id: "hj-stellv-002",
    pathId: "hagen-jura",
    mode: "ausbildung",
    kind: "single",
    topic: "BGB AT - Stellvertretung",
    title: "Verdeckter Handelnder (§ 164 Abs. 2 BGB)",
    prompt:
      "V will im Namen des G handeln, lässt diesen Willen aber nicht erkennen. Wie wirkt sein Geschäft?",
    options: [
      "Das Geschäft trifft G, weil V tatsächlich für ihn handeln wollte.",
      "Das Geschäft ist schwebend unwirksam bis zur Genehmigung des G.",
      "Das Geschäft trifft V selbst; sein nicht erkennbarer Wille, für G zu handeln, ist unbeachtlich und nicht anfechtbar.",
      "V kann seine Erklärung wegen Irrtums nach § 119 Abs. 1 BGB anfechten.",
    ],
    answers: [2],
    explanation:
      "§ 164 Abs. 2 BGB: Tritt der Wille, in fremdem Namen zu handeln, nicht erkennbar hervor, so kommt der Mangel des Willens, im eigenen Namen zu handeln, nicht in Betracht. Das Geschäft trifft den Handelnden selbst; eine Anfechtung wegen dieses Irrtums ist ausgeschlossen.",
    sourceIds: ["bgb164"],
    sources: [{ id: "bgb164", label: "§ 164 BGB", url: "https://www.gesetze-im-internet.de/bgb/__164.html" }],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Subsumtion",
    skillTags: ["bgb-at", "stellvertretung"],
    reviewStatus: "beta-ready",
  },
  {
    id: "hj-stellv-003",
    pathId: "hagen-jura",
    mode: "ausbildung",
    kind: "single",
    topic: "BGB AT - Stellvertretung",
    title: "Geschäft für den, den es angeht",
    prompt:
      "Wann ist das Offenkundigkeitsprinzip ausnahmsweise entbehrlich (sog. Geschäft für den, den es angeht)?",
    options: [
      "Bei Bargeschäften des täglichen Lebens, bei denen dem Geschäftsgegner gleichgültig ist, mit wem er kontrahiert.",
      "Bei allen Geschäften unter Kaufleuten.",
      "Bei notariell beurkundeten Geschäften.",
      "Nie - Offenkundigkeit ist ausnahmslos erforderlich.",
    ],
    answers: [0],
    explanation:
      "Beim offenen Geschäft für den, den es angeht, tritt die Rechtsfolge unmittelbar beim Vertretenen ein, obwohl der Name nicht offengelegt wird. Anerkannt ist dies vor allem bei Bargeschäften des täglichen Lebens, bei denen es dem Vertragspartner gleichgültig ist, wer Vertragspartner wird (Ausnahme zum Offenkundigkeitsprinzip des § 164 Abs. 1 BGB).",
    sourceIds: ["bgb164"],
    sources: [{ id: "bgb164", label: "§ 164 BGB", url: "https://www.gesetze-im-internet.de/bgb/__164.html" }],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Ausnahme",
    skillTags: ["bgb-at", "stellvertretung"],
    reviewStatus: "beta-ready",
  },
  {
    id: "hj-stellv-004",
    pathId: "hagen-jura",
    mode: "ausbildung",
    kind: "single",
    topic: "BGB AT - Stellvertretung",
    title: "Stellvertreter vs. Bote",
    prompt: "Worin liegt der entscheidende Unterschied zwischen Stellvertreter und Bote?",
    options: [
      "Der Bote benötigt eine Vollmacht, der Stellvertreter nicht.",
      "Der Stellvertreter gibt eine eigene Willenserklärung ab, der Bote übermittelt nur eine fremde, fertige Willenserklärung.",
      "Der Unterschied ist rein mengenmäßig (Anzahl der Geschäfte).",
      "Der Bote muss voll geschäftsfähig sein, der Stellvertreter nicht.",
    ],
    answers: [1],
    explanation:
      "Der Stellvertreter (§ 164 BGB) bildet und äußert einen eigenen rechtsgeschäftlichen Willen; der Bote übermittelt lediglich eine bereits fertige fremde Willenserklärung. Folge u.a.: Auf Willensmängel und Geschäftsfähigkeit kommt es beim Vertreter auf seine Person an (§§ 165, 166), beim Boten auf den Geschäftsherrn.",
    sourceIds: ["bgb164"],
    sources: [{ id: "bgb164", label: "§ 164 BGB", url: "https://www.gesetze-im-internet.de/bgb/__164.html" }],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Abgrenzung",
    skillTags: ["bgb-at", "stellvertretung"],
    reviewStatus: "beta-ready",
  },
  {
    id: "hj-stellv-005",
    pathId: "hagen-jura",
    mode: "ausbildung",
    kind: "single",
    topic: "BGB AT - Stellvertretung",
    title: "Beschränkt geschäftsfähiger Vertreter (§ 165 BGB)",
    prompt: "Kann ein nur beschränkt Geschäftsfähiger wirksam als Stellvertreter handeln?",
    options: [
      "Nein, ein Vertreter muss stets voll geschäftsfähig sein.",
      "Nur, wenn der Geschäftsgegner zustimmt.",
      "Ja - die Wirksamkeit der Vertretererklärung wird durch die beschränkte Geschäftsfähigkeit nicht berührt (§ 165 BGB).",
      "Nur bei lediglich rechtlich vorteilhaften Geschäften.",
    ],
    answers: [2],
    explanation:
      "§ 165 BGB: Die Wirksamkeit einer von dem oder gegenüber dem Vertreter abgegebenen Willenserklärung wird nicht dadurch beeinträchtigt, dass der Vertreter in der Geschäftsfähigkeit beschränkt ist. Die Rechtsfolgen treffen den Vertretenen, nicht den Minderjährigen - dieser bedarf daher keines Schutzes durch volle Geschäftsfähigkeit.",
    sourceIds: ["bgb165"],
    sources: [{ id: "bgb165", label: "§ 165 BGB", url: "https://www.gesetze-im-internet.de/bgb/__165.html" }],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Definition",
    skillTags: ["bgb-at", "stellvertretung"],
    reviewStatus: "beta-ready",
  },
  {
    id: "hj-stellv-006",
    pathId: "hagen-jura",
    mode: "ausbildung",
    kind: "single",
    topic: "BGB AT - Stellvertretung",
    title: "Wissenszurechnung (§ 166 Abs. 1 BGB)",
    prompt:
      "Auf wessen Kenntnis bzw. Willensmängel kommt es grundsätzlich an, wenn diese rechtserheblich sind?",
    options: [
      "Auf die Person des Vertreters, nicht des Vertretenen (§ 166 Abs. 1 BGB).",
      "Stets auf die Person des Vertretenen.",
      "Auf beide kumulativ.",
      "Auf die Person des Geschäftsgegners.",
    ],
    answers: [0],
    explanation:
      "§ 166 Abs. 1 BGB: Soweit die rechtlichen Folgen einer Willenserklärung durch Willensmängel oder durch die Kenntnis bzw. das Kennenmüssen gewisser Umstände beeinflusst werden, kommt nicht die Person des Vertretenen, sondern die des Vertreters in Betracht (Ausnahme: § 166 Abs. 2 BGB bei weisungsgebundenem Handeln).",
    sourceIds: ["bgb166"],
    sources: [{ id: "bgb166", label: "§ 166 BGB", url: "https://www.gesetze-im-internet.de/bgb/__166.html" }],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Definition",
    skillTags: ["bgb-at", "stellvertretung"],
    reviewStatus: "beta-ready",
  },
  {
    id: "hj-stellv-007",
    pathId: "hagen-jura",
    mode: "ausbildung",
    kind: "single",
    topic: "BGB AT - Stellvertretung",
    title: "Weisungsgebundenes Handeln (§ 166 Abs. 2 BGB)",
    prompt:
      "Der Vertreter handelt nach bestimmten Weisungen des Vollmachtgebers. Kann sich dieser auf die Unkenntnis des Vertreters berufen?",
    options: [
      "Ja, immer - maßgeblich ist allein die Person des Vertreters.",
      "Nein - bezüglich Umständen, die der Vollmachtgeber selbst kannte, kann er sich nicht auf die Unkenntnis des Vertreters berufen (§ 166 Abs. 2 BGB).",
      "Nur bei arglistigem Verhalten des Vollmachtgebers.",
      "Die Frage ist gegenstandslos, weil § 166 keinen Absatz 2 enthält.",
    ],
    answers: [1],
    explanation:
      "§ 166 Abs. 2 BGB: Hat der Vertreter nach bestimmten Weisungen des Vollmachtgebers gehandelt, kann sich dieser hinsichtlich solcher Umstände, die er selbst kannte, nicht auf die Unkenntnis des Vertreters berufen; Gleiches gilt für Umstände, die er kennen musste.",
    sourceIds: ["bgb166"],
    sources: [{ id: "bgb166", label: "§ 166 BGB", url: "https://www.gesetze-im-internet.de/bgb/__166.html" }],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Subsumtion",
    skillTags: ["bgb-at", "stellvertretung"],
    reviewStatus: "beta-ready",
  },
  {
    id: "hj-stellv-008",
    pathId: "hagen-jura",
    mode: "ausbildung",
    kind: "single",
    topic: "BGB AT - Stellvertretung",
    title: "Innen- und Außenvollmacht (§ 167 Abs. 1 BGB)",
    prompt: "Wie kann eine Vollmacht nach § 167 Abs. 1 BGB erteilt werden?",
    options: [
      "Nur durch öffentliche Bekanntmachung.",
      "Nur gegenüber einem Notar.",
      "Durch Erklärung gegenüber dem Bevollmächtigten (Innenvollmacht) oder gegenüber dem Dritten (Außenvollmacht).",
      "Nur schriftlich gegenüber dem Dritten.",
    ],
    answers: [2],
    explanation:
      "§ 167 Abs. 1 BGB: Die Erteilung der Vollmacht erfolgt durch Erklärung gegenüber dem zu Bevollmächtigenden (Innenvollmacht) oder gegenüber dem Dritten, dem gegenüber die Vertretung stattfinden soll (Außenvollmacht).",
    sourceIds: ["bgb167"],
    sources: [{ id: "bgb167", label: "§ 167 BGB", url: "https://www.gesetze-im-internet.de/bgb/__167.html" }],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Definition",
    skillTags: ["bgb-at", "stellvertretung"],
    reviewStatus: "beta-ready",
  },
  {
    id: "hj-stellv-009",
    pathId: "hagen-jura",
    mode: "ausbildung",
    kind: "single",
    topic: "BGB AT - Stellvertretung",
    title: "Form der Vollmacht (§ 167 Abs. 2 BGB)",
    prompt:
      "Bedarf die Vollmacht der Form, die für das Geschäft gilt, auf das sie sich bezieht?",
    options: [
      "Ja, sie muss stets dieselbe Form wahren wie das Hauptgeschäft.",
      "Grundsätzlich nicht - die Vollmacht ist nach § 167 Abs. 2 BGB formfrei, auch wenn das Hauptgeschäft formbedürftig ist.",
      "Sie bedarf immer der Schriftform.",
      "Sie bedarf immer der notariellen Beurkundung.",
    ],
    answers: [1],
    explanation:
      "§ 167 Abs. 2 BGB: Die Erklärung der Bevollmächtigung bedarf nicht der Form, die für das Rechtsgeschäft bestimmt ist, auf das sich die Vollmacht bezieht. Rechtsprechung und Lehre machen hiervon nur enge Ausnahmen (z.B. unwiderrufliche Vollmacht zum Grundstückskauf), um den Formzweck nicht zu umgehen.",
    sourceIds: ["bgb167"],
    sources: [{ id: "bgb167", label: "§ 167 BGB", url: "https://www.gesetze-im-internet.de/bgb/__167.html" }],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Definition",
    skillTags: ["bgb-at", "stellvertretung"],
    reviewStatus: "beta-ready",
  },
  {
    id: "hj-stellv-010",
    pathId: "hagen-jura",
    mode: "ausbildung",
    kind: "single",
    topic: "BGB AT - Stellvertretung",
    title: "Duldungs- vs. Anscheinsvollmacht",
    prompt: "Worin unterscheiden sich Duldungs- und Anscheinsvollmacht?",
    options: [
      "Es besteht kein Unterschied; beide sind dasselbe.",
      "Bei der Duldungsvollmacht kennt und duldet der Vertretene das Auftreten des Vertreters; bei der Anscheinsvollmacht kennt er es nicht, hätte es bei pflichtgemäßer Sorgfalt aber erkennen und verhindern können.",
      "Die Anscheinsvollmacht ist ausdrücklich in § 172 BGB geregelt, die Duldungsvollmacht nicht.",
      "Die Duldungsvollmacht bedarf der Schriftform.",
    ],
    answers: [1],
    explanation:
      "Beide sind gewohnheitsrechtlich anerkannte Rechtsscheinvollmachten (Anknüpfung an §§ 171–173 BGB). Duldungsvollmacht: Der Vertretene kennt das Vertreterhandeln und duldet es. Anscheinsvollmacht: Der Vertretene kennt es nicht, hätte es aber bei pflichtgemäßer Sorgfalt erkennen und verhindern können; der Dritte durfte auf die Bevollmächtigung vertrauen.",
    sourceIds: ["bgb172"],
    sources: [{ id: "bgb172", label: "§ 172 BGB", url: "https://www.gesetze-im-internet.de/bgb/__172.html" }],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Abgrenzung",
    skillTags: ["bgb-at", "stellvertretung"],
    reviewStatus: "beta-ready",
  },
  {
    id: "hj-stellv-011",
    pathId: "hagen-jura",
    mode: "ausbildung",
    kind: "single",
    topic: "BGB AT - Stellvertretung",
    title: "Einseitiges Rechtsgeschäft ohne Vollmachtsurkunde (§ 174 BGB)",
    prompt:
      "V spricht namens des G eine Kündigung gegenüber K aus, ohne eine Vollmachtsurkunde vorzulegen. Was kann K tun?",
    options: [
      "Nichts - einseitige Rechtsgeschäfte sind stets wirksam.",
      "Nur Schadensersatz verlangen.",
      "Das Rechtsgeschäft unverzüglich zurückweisen; dann ist es unwirksam (§ 174 S. 1 BGB), sofern G den K nicht von der Bevollmächtigung in Kenntnis gesetzt hatte.",
      "Die Kündigung ist schwebend unwirksam und von G genehmigungsfähig.",
    ],
    answers: [2],
    explanation:
      "§ 174 S. 1 BGB: Ein einseitiges Rechtsgeschäft, das ein Bevollmächtigter vornimmt, ist unwirksam, wenn er keine Vollmachtsurkunde vorlegt und der andere das Rechtsgeschäft aus diesem Grund unverzüglich zurückweist. Nach S. 2 ist die Zurückweisung ausgeschlossen, wenn der Vollmachtgeber den anderen von der Bevollmächtigung in Kenntnis gesetzt hatte.",
    sourceIds: ["bgb174"],
    sources: [{ id: "bgb174", label: "§ 174 BGB", url: "https://www.gesetze-im-internet.de/bgb/__174.html" }],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Subsumtion",
    skillTags: ["bgb-at", "stellvertretung"],
    reviewStatus: "beta-ready",
  },
  {
    id: "hj-stellv-012",
    pathId: "hagen-jura",
    mode: "ausbildung",
    kind: "single",
    topic: "BGB AT - Stellvertretung",
    title: "Einseitiges Rechtsgeschäft ohne Vertretungsmacht (§ 180 BGB)",
    prompt:
      "V nimmt ohne Vertretungsmacht ein einseitiges Rechtsgeschäft im Namen des G vor. Wie ist dies grundsätzlich zu beurteilen?",
    options: [
      "Schwebend unwirksam und durch G genehmigungsfähig wie bei Verträgen.",
      "Grundsätzlich unzulässig (§ 180 S. 1 BGB): Die §§ 177–179 gelten nicht, das Geschäft ist unwirksam - vorbehaltlich der Ausnahmen des § 180 S. 2, 3.",
      "Voll wirksam, weil einseitige Rechtsgeschäfte keine Vertretungsmacht voraussetzen.",
      "Anfechtbar nach § 119 BGB.",
    ],
    answers: [1],
    explanation:
      "§ 180 S. 1 BGB: Bei einem einseitigen Rechtsgeschäft ist Vertretung ohne Vertretungsmacht grundsätzlich unzulässig; die §§ 177–179 (Genehmigung, Vertreterhaftung) sind nicht anwendbar, das Geschäft ist unwirksam. Ausnahmen: § 180 S. 2, 3 (Einverständnis bzw. Nichtbeanstandung des Erklärungsempfängers, empfangsbedürftige Erklärungen).",
    sourceIds: ["bgb180"],
    sources: [{ id: "bgb180", label: "§ 180 BGB", url: "https://www.gesetze-im-internet.de/bgb/__180.html" }],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Subsumtion",
    skillTags: ["bgb-at", "stellvertretung"],
    reviewStatus: "beta-ready",
  },
  {
    id: "hj-stellv-013",
    pathId: "hagen-jura",
    mode: "ausbildung",
    kind: "single",
    topic: "BGB AT - Stellvertretung",
    title: "Insichgeschäft (§ 181 BGB)",
    prompt: "Unter welchen Voraussetzungen ist ein Insichgeschäft nach § 181 BGB ausnahmsweise zulässig?",
    options: [
      "Niemals.",
      "Immer bei Bargeschäften.",
      "Wenn es dem Vertreter gestattet ist oder das Geschäft ausschließlich in der Erfüllung einer Verbindlichkeit besteht (§ 181 BGB).",
      "Nur bei notarieller Beurkundung.",
    ],
    answers: [2],
    explanation:
      "§ 181 BGB: Ein Vertreter kann grundsätzlich nicht im Namen des Vertretenen mit sich selbst (Selbstkontrahieren) oder als Vertreter eines Dritten (Mehrvertretung) ein Rechtsgeschäft vornehmen. Ausnahmen: Das Geschäft ist gestattet oder besteht ausschließlich in der Erfüllung einer Verbindlichkeit. Zweck ist die Vermeidung von Interessenkonflikten.",
    sourceIds: ["bgb181"],
    sources: [{ id: "bgb181", label: "§ 181 BGB", url: "https://www.gesetze-im-internet.de/bgb/__181.html" }],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Ausnahme",
    skillTags: ["bgb-at", "stellvertretung"],
    reviewStatus: "beta-ready",
  },
  {
    id: "hj-stellv-014",
    pathId: "hagen-jura",
    mode: "ausbildung",
    kind: "single",
    topic: "BGB AT - Stellvertretung",
    title: "Missbrauch der Vertretungsmacht - Kollusion",
    prompt:
      "Vertreter und Geschäftsgegner wirken bewusst zum Nachteil des Vertretenen zusammen (Kollusion). Wie ist das Geschäft zu beurteilen?",
    options: [
      "Wirksam, weil die Vertretungsmacht im Außenverhältnis bestand.",
      "Sittenwidrig und nichtig nach § 138 Abs. 1 BGB.",
      "Schwebend unwirksam bis zur Genehmigung des Vertretenen.",
      "Nur nach § 123 BGB anfechtbar.",
    ],
    answers: [1],
    explanation:
      "Beim kollusiven Zusammenwirken von Vertreter und Drittem zum Schaden des Vertretenen ist das Geschäft nach § 138 Abs. 1 BGB sittenwidrig und nichtig. Davon zu unterscheiden ist die bloße Evident-Überschreitung (Vollmachtsmissbrauch), bei der dem Dritten nach § 242 BGB die Berufung auf die Vertretungsmacht versagt ist.",
    sourceIds: ["bgb138"],
    sources: [{ id: "bgb138", label: "§ 138 BGB", url: "https://www.gesetze-im-internet.de/bgb/__138.html" }],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Subsumtion",
    skillTags: ["bgb-at", "stellvertretung"],
    reviewStatus: "beta-ready",
  },
];
